import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Upload, X, Image as ImageIcon, FileText, Trash2 } from 'lucide-react';
import { assetService } from '@services/assetService';
import { companyService } from '@services/companyService';
import { clientService } from '@services/clientService';
import CustomSelect from '../shared/CustomSelect';

const AssetFormPage = () => {
    const { id, clientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = !!id;

    // Form state
    const [formData, setFormData] = useState({
        clientId: clientId || '',
        assetRequirementId: '',
        attributes: '{}',
        notes: ''
    });

    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [assetRequirements, setAssetRequirements] = useState([]);
    const [requirementsLoading, setRequirementsLoading] = useState(false);
    const [selectedClientType, setSelectedClientType] = useState('');
    const [attributesJson, setAttributesJson] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Vehicle make/model state
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [selectedMakeId, setSelectedMakeId] = useState('');
    const [makesLoading, setMakesLoading] = useState(false);
    const [modelsLoading, setModelsLoading] = useState(false);

    // Document upload state
    const [uploadFiles, setUploadFiles] = useState([]);
    const [documentType, setDocumentType] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const DOCUMENT_TYPE_OPTIONS = [
        { value: 'LOGBOOK', label: 'Logbook' },
        { value: 'TITLE_DEED', label: 'Title Deed' },
        { value: 'REGISTRATION', label: 'Registration' },
        { value: 'INSURANCE', label: 'Insurance' },
        { value: 'VALUATION', label: 'Valuation Report' },
        { value: 'PHOTO', label: 'Photo' },
        { value: 'OTHER', label: 'Other' }
    ];

    const DEFAULT_DOC_TYPE_MAP = {
        VEHICLE: 'LOGBOOK',
        LAND: 'TITLE_DEED',
        PROPERTY: 'TITLE_DEED'
    };

    useEffect(() => {
        fetchClients();
        fetchCompanies();
        if (isEditMode) {
            fetchAssetDetails();
        }
        // If clientId is pre-set (from URL or edit), fetch that specific client
        if (clientId) {
            fetchSpecificClient(clientId);
        }
    }, []);

    useEffect(() => {
        if (selectedCompany && selectedClientType) {
            setAssetRequirements([]);
            fetchAssetRequirements();
        }
    }, [selectedCompany, selectedClientType]);

    useEffect(() => {
        if (formData.clientId && clients.length > 0) {
            const client = clients.find(c => c.id.toString() === formData.clientId.toString());
            if (client) {
                setSelectedClientType(client.clientType);
            }
        }
    }, [formData.clientId, clients]);

    const fetchClients = async () => {
        try {
            const response = await clientService.getPaginated({ page: 0, size: 50 });
            if (response.success && response.data) {
                const list = response.data.content || response.data;
                setClients(prev => {
                    const newList = Array.isArray(list) ? list : [];
                    // Merge with any previously fetched specific client
                    const existingIds = new Set(newList.map(c => c.id));
                    const extras = prev.filter(c => !existingIds.has(c.id));
                    return [...extras, ...newList];
                });
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    const fetchSpecificClient = async (id) => {
        try {
            const response = await clientService.getById(id);
            if (response.success && response.data) {
                setClients(prev => {
                    const exists = prev.some(c => c.id.toString() === id.toString());
                    return exists ? prev : [response.data, ...prev];
                });
            }
        } catch (error) {
            console.error('Error fetching specific client:', error);
        }
    };

    const searchClients = async (query) => {
        if (!query || query.length < 2) {
            fetchClients();
            return;
        }
        try {
            setClientsLoading(true);
            const response = await clientService.searchClients(query);
            if (response.success && response.data) {
                const list = response.data.content || response.data;
                const searchResults = Array.isArray(list) ? list : [];
                // Preserve the currently selected client in the list
                setClients(prev => {
                    if (formData.clientId) {
                        const selected = prev.find(c => c.id.toString() === formData.clientId.toString());
                        if (selected && !searchResults.some(c => c.id.toString() === selected.id.toString())) {
                            return [selected, ...searchResults];
                        }
                    }
                    return searchResults;
                });
            }
        } catch (error) {
            console.error('Error searching clients:', error);
        } finally {
            setClientsLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            if (response.success && response.data) {
                setCompanies(response.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchAssetRequirements = async () => {
        try {
            setRequirementsLoading(true);
            const response = await assetService.assetRequirements.getByCompanyAndClientType(
                selectedCompany,
                selectedClientType
            );
            if (response.success && response.data) {
                setAssetRequirements(response.data);
            }
        } catch (error) {
            console.error('Error fetching asset requirements:', error);
        } finally {
            setRequirementsLoading(false);
        }
    };

    const fetchAssetDetails = async () => {
        try {
            setLoading(true);
            const response = await assetService.clientAssets.getById(id);
            if (response.success && response.data) {
                const asset = response.data;
                setFormData({
                    clientId: asset.clientId,
                    assetRequirementId: asset.assetRequirementId,
                    attributes: asset.attributes || '{}',
                    notes: asset.notes || ''
                });

                // Ensure the asset's client is in the dropdown
                if (asset.clientId) {
                    fetchSpecificClient(asset.clientId);
                }

                // Set company so asset requirements load in edit mode
                if (asset.companyId) {
                    setSelectedCompany(asset.companyId.toString());
                }

                // Set document type default based on asset type
                if (asset.assetType) {
                    setDocumentType(DEFAULT_DOC_TYPE_MAP[asset.assetType] || 'OTHER');
                }

                try {
                    setAttributesJson(JSON.parse(asset.attributes || '{}'));
                } catch (e) {
                    setAttributesJson({});
                }
            }
        } catch (error) {
            console.error('Error fetching asset:', error);
            setError('Failed to load asset details');
        } finally {
            setLoading(false);
        }
    };

    // Determine if selected requirement is VEHICLE type
    const getSelectedRequirement = () => {
        if (!formData.assetRequirementId) return null;
        return assetRequirements.find(
            r => r.id.toString() === formData.assetRequirementId.toString()
        );
    };

    const isVehicleAsset = () => {
        const req = getSelectedRequirement();
        return req?.assetType === 'VEHICLE';
    };

    // Auto-set document type based on asset type
    useEffect(() => {
        const req = getSelectedRequirement();
        if (req?.assetType) {
            setDocumentType(DEFAULT_DOC_TYPE_MAP[req.assetType] || 'OTHER');
        }
    }, [formData.assetRequirementId, assetRequirements]);

    // Fetch vehicle makes when asset type is VEHICLE
    useEffect(() => {
        if (isVehicleAsset() && vehicleMakes.length === 0) {
            fetchVehicleMakes();
        }
    }, [formData.assetRequirementId, assetRequirements]);

    // Fetch models when make changes
    useEffect(() => {
        if (selectedMakeId) {
            setVehicleModels([]);
            fetchVehicleModels(selectedMakeId);
        }
    }, [selectedMakeId]);

    // Sync selectedMakeId from attributesJson (for edit mode)
    useEffect(() => {
        if (isVehicleAsset() && attributesJson.make && vehicleMakes.length > 0 && !selectedMakeId) {
            const make = vehicleMakes.find(m => m.name === attributesJson.make);
            if (make) {
                setSelectedMakeId(make.id);
            }
        }
    }, [attributesJson.make, vehicleMakes]);

    const fetchVehicleMakes = async () => {
        try {
            setMakesLoading(true);
            const response = await assetService.vehicleData.getMakes();
            if (response.success && response.data) {
                setVehicleMakes(response.data);
            }
        } catch (err) {
            console.error('Error fetching vehicle makes:', err);
        } finally {
            setMakesLoading(false);
        }
    };

    const fetchVehicleModels = async (makeId) => {
        try {
            setModelsLoading(true);
            const response = await assetService.vehicleData.getModels(makeId);
            if (response.success && response.data) {
                setVehicleModels(response.data);
            }
        } catch (err) {
            console.error('Error fetching vehicle models:', err);
        } finally {
            setModelsLoading(false);
        }
    };

    const handleMakeChange = (makeId) => {
        const make = vehicleMakes.find(m => m.id.toString() === makeId.toString());
        setSelectedMakeId(makeId);
        // Update make and clear model in one go
        const updated = { ...attributesJson, make: make?.name || '', model: '' };
        setAttributesJson(updated);
        setFormData(prev => ({ ...prev, attributes: JSON.stringify(updated) }));
    };

    const handleModelChange = (modelName) => {
        handleAttributeChange('model', modelName);
    };

    const handleAttributeChange = (key, value) => {
        const updated = { ...attributesJson, [key]: value };
        setAttributesJson(updated);
        setFormData(prev => ({
            ...prev,
            attributes: JSON.stringify(updated)
        }));
    };

    // Document upload handlers
    const handleFileSelect = (files) => {
        const validFiles = Array.from(files).filter(file => {
            const maxSize = 10 * 1024 * 1024; // 10MB
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (file.size > maxSize) {
                setError(`File "${file.name}" exceeds 10MB limit`);
                return false;
            }
            if (!allowedTypes.includes(file.type)) {
                setError(`File "${file.name}" is not a supported format (PDF, JPEG, PNG)`);
                return false;
            }
            return true;
        });
        setUploadFiles(prev => [...prev, ...validFiles]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files?.length) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const removeFile = (index) => {
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getAttributeFields = () => {
        const requirement = assetRequirements.find(
            r => r.id.toString() === formData.assetRequirementId.toString()
        );

        if (!requirement || !requirement.requiredAttributes) return [];

        try {
            return JSON.parse(requirement.requiredAttributes);
        } catch (e) {
            return [];
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate vehicle required fields
        if (isVehicleAsset()) {
            const missing = [];
            if (!attributesJson.chassis?.trim()) missing.push('Chassis Number');
            if (!attributesJson.licensePlate?.trim()) missing.push('License Plate');
            if (!attributesJson.make?.trim()) missing.push('Make');
            if (!attributesJson.model?.trim()) missing.push('Model');
            if (missing.length > 0) {
                setError(`Please fill in required fields: ${missing.join(', ')}`);
                return;
            }
        }

        setSaving(true);
        setError('');

        try {
            let response;
            if (isEditMode) {
                response = await assetService.clientAssets.update(id, formData);
            } else {
                response = await assetService.clientAssets.submit(formData);
            }

            if (response.success) {
                // Upload documents if any were added
                const assetId = isEditMode ? id : response.data?.id;
                if (assetId && uploadFiles.length > 0) {
                    for (const file of uploadFiles) {
                        try {
                            await assetService.assetDocuments.uploadWithFile(
                                assetId, file, documentType, ''
                            );
                        } catch (uploadErr) {
                            console.error('Error uploading document:', uploadErr);
                        }
                    }
                }

                if (clientId) {
                    navigate(`/clients/${clientId}`);
                } else {
                    navigate('/compliance/assets');
                }
            } else {
                setError(response.message || 'Failed to save asset');
            }
        } catch (error) {
            console.error('Error saving asset:', error);
            setError(error.message || 'Failed to save asset');
        } finally {
            setSaving(false);
        }
    };

    const clientOptions = clients.map(client => ({
        value: client.id,
        label: client.clientType === 'CORPORATE'
            ? client.companyName || client.name
            : `${client.firstName} ${client.lastName}`
    }));

    const companyOptions = companies.map(company => ({
        value: company.id,
        label: company.name
    }));

    const requirementOptions = assetRequirements.map(req => ({
        value: req.id,
        label: `${req.name} (${req.assetType})`
    }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-start py-10 px-4 bg-gray-50">
            <div className="w-full max-w-4xl space-y-6">

            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => clientId ? navigate(`/clients/${clientId}`) : navigate('/compliance/assets')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {isEditMode ? 'Edit Asset' : 'Add New Asset'}
                    </h2>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    {/* Client Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Client <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                            value={formData.clientId}
                            onChange={(value) => setFormData(prev => ({ ...prev, clientId: value }))}
                            options={clientOptions}
                            placeholder="Select a client"
                            searchable
                            disabled={!!clientId || isEditMode}
                            error={!formData.clientId}
                            onSearch={searchClients}
                            loading={clientsLoading}
                        />
                    </div>

                    {/* Company Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                            options={companyOptions}
                            placeholder="Select a company"
                            searchable
                            disabled={isEditMode}
                        />
                    </div>

                    {/* Asset Requirement Selection */}
                    {selectedCompany && selectedClientType && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Asset Type <span className="text-red-500">*</span>
                            </label>
                            {requirementsLoading ? (
                                <div className="flex items-center gap-2 py-2 px-3 text-sm text-gray-500">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Loading asset types...
                                </div>
                            ) : assetRequirements.length === 0 ? (
                                <div className="text-sm text-amber-600 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    No asset types configured for this company and client type.{' '}
                                    <a href="/compliance/assets/requirements" className="underline font-medium hover:text-amber-800">
                                        Configure Asset Requirements →
                                    </a>
                                </div>
                            ) : (
                                <CustomSelect
                                    value={formData.assetRequirementId}
                                    onChange={(value) => setFormData(prev => ({ ...prev, assetRequirementId: value }))}
                                    options={requirementOptions}
                                    placeholder="Select asset type"
                                    searchable
                                    disabled={isEditMode}
                                    error={!formData.assetRequirementId}
                                />
                            )}
                        </div>
                    )}

                    {/* Dynamic Attribute Fields */}
                    {formData.assetRequirementId && getAttributeFields().length > 0 && (
                        <div className="border-t pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Asset Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {getAttributeFields().map((field, index) => {
                                    const vehicleRequired = isVehicleAsset() && ['chassis', 'licensePlate', 'make', 'model'].includes(field);
                                    const fieldLabel = field
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/\b\w/g, c => c.toUpperCase())
                                        .trim();

                                    // Vehicle Make dropdown
                                    if (isVehicleAsset() && field === 'make') {
                                        return (
                                            <div key={index}>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Make <span className="text-red-500">*</span>
                                                </label>
                                                <CustomSelect
                                                    value={selectedMakeId}
                                                    onChange={handleMakeChange}
                                                    options={vehicleMakes.map(m => ({
                                                        value: m.id,
                                                        label: m.name
                                                    }))}
                                                    placeholder="Select vehicle make"
                                                    searchable
                                                    loading={makesLoading}
                                                    error={!attributesJson.make}
                                                />
                                            </div>
                                        );
                                    }

                                    // Vehicle Model dropdown (conditional on make)
                                    if (isVehicleAsset() && field === 'model') {
                                        return (
                                            <div key={index}>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Model <span className="text-red-500">*</span>
                                                </label>
                                                <CustomSelect
                                                    value={attributesJson.model || ''}
                                                    onChange={handleModelChange}
                                                    options={vehicleModels.map(m => ({
                                                        value: m.name,
                                                        label: m.name
                                                    }))}
                                                    placeholder={selectedMakeId ? "Select vehicle model" : "Select a make first"}
                                                    searchable
                                                    disabled={!selectedMakeId}
                                                    loading={modelsLoading}
                                                    error={selectedMakeId && !attributesJson.model}
                                                />
                                            </div>
                                        );
                                    }

                                    // Year of Manufacture dropdown
                                    if (isVehicleAsset() && field === 'yearOfManufacture') {
                                        const currentYear = new Date().getFullYear();
                                        const yearOptions = [];
                                        for (let y = currentYear + 1; y >= 1980; y--) {
                                            yearOptions.push({ value: String(y), label: String(y) });
                                        }
                                        return (
                                            <div key={index}>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Year of Manufacture
                                                </label>
                                                <CustomSelect
                                                    value={attributesJson.yearOfManufacture || ''}
                                                    onChange={(value) => handleAttributeChange('yearOfManufacture', value)}
                                                    options={yearOptions}
                                                    placeholder="Select year"
                                                    searchable
                                                />
                                            </div>
                                        );
                                    }

                                    // Default text input for other fields
                                    return (
                                        <div key={index}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {fieldLabel}
                                                {vehicleRequired && <span className="text-red-500"> *</span>}
                                            </label>
                                            <input
                                                type="text"
                                                value={attributesJson[field] || ''}
                                                onChange={(e) => handleAttributeChange(field, e.target.value)}
                                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    vehicleRequired && !attributesJson[field] ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder={`Enter ${fieldLabel}`}
                                                required={vehicleRequired}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notes
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            rows="4"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Add any additional notes..."
                        />
                    </div>

                    {/* Document Upload */}
                    {formData.assetRequirementId && (
                        <div className="border-t pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Documents</h3>

                            {/* Document Type Selector */}
                            <div className="mb-4 max-w-xs">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document Type
                                </label>
                                <select
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {DOCUMENT_TYPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Drop Zone */}
                            <div
                                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                                    dragActive
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                                }`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                                onDragLeave={() => setDragActive(false)}
                                onDrop={handleDrop}
                            >
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-600">
                                    Drag & drop files here, or <span className="text-blue-600 font-medium">browse</span>
                                </p>
                                <p className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG up to 10MB</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => {
                                        handleFileSelect(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </div>

                            {/* File List */}
                            {uploadFiles.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {uploadFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {file.type.startsWith('image/')
                                                    ? <ImageIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                    : <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                                                }
                                                <span className="text-sm text-gray-700 truncate">{file.name}</span>
                                                <span className="text-xs text-gray-400 flex-shrink-0">
                                                    {formatFileSize(file.size)}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Submit Buttons */}
                    <div className="flex items-center gap-3 pt-6 border-t">
                        <button
                            type="submit"
                            disabled={saving || !formData.clientId || !formData.assetRequirementId}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? 'Update Asset' : 'Create Asset'}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => clientId ? navigate(`/clients/${clientId}`) : navigate('/compliance/assets')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
        </div>
    );
};

export default AssetFormPage;
